// React
import type { KeyboardEvent } from 'react';

// Icons
import { ChevronIcon } from '@/react/components/icons/ChevronIcon';
import { CloseIcon } from '@/react/components/icons/CloseIcon';

// UI primitives
import { Badge } from '@/components/ui/badge';
import { Counter } from '@/components/ui/counter';
import { StatusBadge, STATUS_BADGE_COMPACT } from '@/components/ui/status-badge';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { MobilePrimaryButton } from '../layouts/MobileButtons';
import { cn } from '@/lib/utils';

// Hooks and utilities
import { useTranslation } from '@hooks/useTranslation';
import { formatShortDate } from '@/react/utils/formatters';

// Types
import type { ApiEvent } from '@api/events';
import { createEventStatusBadgeConfig, resolveEventStatusKey } from './event-info-config';

import { INTERPUNCT, CARD_RADIUS, toSentenceCase } from './event-card-shared';
import {
  EventTypeMeta,
  EventServingAndTimings,
  EventAddressAndContacts,
  EventDescriptionAndInstructions,
  EventYieldBar,
  SpineOrderCard,
  EventMenuItems,
  SectionSummary,
} from './EventDetailFields';

/**
 * Mobile (<md) event card + its bottom sheet (GRAIN-2351): a compact tappable
 * summary that opens a bottom sheet with the full detail behind a
 * progressive-disclosure accordion. Companion to the desktop inline card in
 * EventsSectionCard.tsx, which renders unchanged at md+.
 */

export function EventCompactCard({
  event,
  titleLabel,
  statusKey,
  statusBadgeConfig,
  onOpen,
}: {
  event: ApiEvent;
  titleLabel: string;
  statusKey: ReturnType<typeof resolveEventStatusKey>;
  statusBadgeConfig: ReturnType<typeof createEventStatusBadgeConfig>;
  onOpen: () => void;
}) {
  const { t, getSection } = useTranslation();
  const eventTypes = getSection('events.event_types') as Record<string, string> | undefined;
  const units = getSection('events.units') as Record<string, string> | undefined;
  const guestsLabel = units?.guests ?? 'guests';
  const formatType = eventTypes?.[event.event_type] ?? (event.event_type ? toSentenceCase(event.event_type) : null);

  // Meta line: event type · guests, then the serving date — GRAIN-2351 compact
  // card spec. Full requirement/tag chips + remaining timings stay in the sheet.
  const metaParts = [
    formatType,
    event.pax_count != null ? `${event.pax_count} ${guestsLabel}` : null,
    event.scheduled_at ? formatShortDate(event.scheduled_at) : null,
  ].filter((part): part is string => Boolean(part));

  const handleKeyDown = (keyboardEvent: KeyboardEvent<HTMLDivElement>) => {
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      keyboardEvent.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      aria-label={`${t('events_section.view_event_details')} ${event.event_id}`.trim()}
      className={cn(
        CARD_RADIUS,
        'border border-dinnerplate-white shadow-event-card p-4 flex items-center justify-between gap-4',
        'cursor-pointer active:bg-dinnerplate-white-light',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30'
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="rounded-xs border-off-black px-2 py-1 font-inter text-xs font-normal leading-tight text-off-black">
            {event.event_id}
          </Badge>
          <span className="truncate font-inter text-sm font-semibold leading-normal text-off-black">
            {titleLabel}
          </span>
          <StatusBadge value={statusKey} config={statusBadgeConfig} className={STATUS_BADGE_COMPACT} />
        </div>
        {metaParts.length > 0 ? (
          <p className="truncate font-inter text-xs font-normal leading-tight text-grey">
            {metaParts.join(INTERPUNCT)}
          </p>
        ) : null}
      </div>
      <ChevronIcon direction="right" className="shrink-0 text-grey" />
    </div>
  );
}

export function EventDetailMobileSheet({
  event,
  titleLabel,
  statusKey,
  statusBadgeConfig,
  open,
  onOpenChange,
  editEventPath,
}: {
  event: ApiEvent;
  titleLabel: string;
  statusKey: ReturnType<typeof resolveEventStatusKey>;
  statusBadgeConfig: ReturnType<typeof createEventStatusBadgeConfig>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Edit is the only event action on phones; omit it to show the sheet read-only.
  editEventPath?: string;
}) {
  const { t } = useTranslation();

  // Collapsed-accordion summaries: a preview of each section's content shown in
  // the header while closed (hidden when the section is expanded).
  const servingSummary = event.scheduled_at ? formatShortDate(event.scheduled_at) : null;
  const description = event.metadata?.description;
  const menuCount = event.line_items?.filter((li) => li.item != null).length ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* hideClose: we render our own SheetClose in the header (matches the
          canonical mobile bottom-sheet recipe used by EventsDateFilterSheet).
          aria-describedby omitted deliberately (see PreviewSheet precedent)
          since SheetTitle alone satisfies Radix's a11y requirement.
          z-[60]: this sheet can open while nested inside OrderDetailSheet's
          own (right-side, z-50) sheet — bump above it so it layers on top;
          the default Portal still targets document.body. */}
      <SheetContent
        side="bottom"
        hideClose
        aria-describedby={undefined}
        overlayClassName="z-[60]"
        className={cn(
          'z-[60] flex max-h-sheet-mobile flex-col rounded-t-input border-dinnerplate-white bg-off-white p-0 pb-safe-bottom',
        )}
      >
        <div className="mx-auto mt-2 h-1 w-12 shrink-0 rounded-full bg-grey-light" />
        <SheetHeader className="shrink-0 flex-row items-center justify-between space-y-0 border-b border-dinnerplate-white p-6 text-left">
          <SheetTitle className="flex min-w-0 flex-1 flex-wrap items-center gap-3 font-inter text-sm font-semibold leading-normal text-off-black">
            <Badge variant="outline" className="rounded-xs border-off-black px-2 py-1 font-inter text-xs font-normal leading-tight text-off-black">
              {event.event_id}
            </Badge>
            <span className="truncate">{titleLabel}</span>
            <StatusBadge value={statusKey} config={statusBadgeConfig} className={STATUS_BADGE_COMPACT} />
          </SheetTitle>
          <SheetClose
            className="shrink-0 rounded-xs text-off-black focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black/30"
            aria-label={t('common.close')}
          >
            <CloseIcon className="size-4" />
          </SheetClose>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {/* Details (type/guests + tags) always visible on top; the rest is a
              progressive-disclosure accordion. The sub-components carry their own
              trailing Separators for the desktop layout, so hide horizontal
              separators inside the accordion — the AccordionItem border-b is the
              single divider (avoids doubled borders). */}
          <EventTypeMeta event={event} tagsSingleLine />
          <Accordion type="single" collapsible className="[&_[data-orientation=horizontal]]:hidden">
            <AccordionItem value="serving_timings">
              <AccordionTrigger className="group">
                {t('events_section.sections.serving_timings')}
                {servingSummary && <SectionSummary>{servingSummary}</SectionSummary>}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <EventServingAndTimings event={event} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="address_contacts">
              <AccordionTrigger className="group">
                {t('events_section.sections.address_contacts')}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <EventAddressAndContacts event={event} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="description">
              <AccordionTrigger className="group">
                {t('events_section.sections.description')}
                {description && <SectionSummary>{description}</SectionSummary>}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <EventDescriptionAndInstructions event={event} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="yield">
              <AccordionTrigger className="group">
                {t('events_section.sections.yield')}
                <SectionSummary>
                  <Badge variant="outline" className="tabular-nums rounded-xs border-yield-positive-border bg-yield-positive-bg px-1.5 py-0.5 font-inter text-xs font-semibold leading-tight text-yield-positive">
                    0.00%
                  </Badge>
                </SectionSummary>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="flex flex-col gap-3">
                  <EventYieldBar />
                  <SpineOrderCard event={event} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="menu_items">
              <AccordionTrigger className="group">
                {t('events_section.sections.menu_items')}
                {menuCount > 0 && (
                  <span className="ml-auto pr-2 group-data-[state=open]:hidden">
                    <Counter value={menuCount} />
                  </span>
                )}
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <EventMenuItems event={event} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {editEventPath && (
          <div className="shrink-0 border-t border-dinnerplate-white p-6">
            <MobilePrimaryButton asChild className="w-full justify-center text-nav">
              <a href={editEventPath}>{t('events_section.dropdown_edit')}</a>
            </MobilePrimaryButton>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
